import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { POPUP_LEVEL } from '@/components/ui/popup-level';

export interface SelectMenuOption {
  value: string;
  /**
   * A NODE, not only a string. The label is render-only here — nothing matches or searches on it —
   * and a block's option can be authored content: an equation, a reaction, a media item's glyph
   * and bracket. Stringifying those is how a picker came to show raw LaTeX where the content it
   * names renders properly two inches away.
   *
   * A caller whose option needs a *name* as well (an accessible name, a stored title) carries that
   * separately; this is what the menu DRAWS.
   */
  label: React.ReactNode;
  /**
   * Offered but not choosable right now, because something else holds the slot — the Answer
   * preset, which only one feedback card can carry.
   *
   * Shown greyed rather than dropped: a missing entry reads as a feature that is gone, where a
   * greyed one says where it went. `aria-disabled` rather than `disabled`, so a reader still
   * meets the option and is told it is unavailable; a `disabled` button is skipped silently,
   * which is the same disappearance in a different costume. Arrow keys still land on it for the
   * same reason, and the click is refused in `pick`.
   */
  disabled?: boolean;
  /**
   * A heading this option sits under, for a list holding two KINDS of thing.
   *
   * Written once above the first option carrying it, so the caller orders the options and the
   * menu only draws the breaks. For the link menu, where "a scenario in this task" and "an
   * earlier answer" are different relationships with different scopes — and a teacher reading
   * a flat list of block names cannot tell which they are choosing.
   */
  group?: string;
  /**
   * A glyph before the label, for a list whose entries are THINGS rather than settings.
   *
   * The scenario link menu is the case: its options are blocks in the task, and "Text 2" alone
   * does not read as a block — a teacher took it for "scenario 2". The block's own library icon
   * beside it is what says which object is being chosen. A plain settings menu passes nothing
   * and is unchanged.
   */
  icon?: React.ReactNode;
}

/**
 * The task-builder's dropdown convention — named `SelectMenu`, not `Dropdown`.
 *
 * "Dropdown" was taken, three times over: it is a BLOCK TYPE a teacher adds to a task, it is that
 * block's own `DropdownTriggerDropdown` for picking which option a feedback fires on, and it was
 * this. One word for a thing a student uses, a thing a teacher configures, and a primitive, which
 * made every sentence about any of them need a qualifier to be read at all.
 *
 * What it is: a bordered `bg-ws-btn` trigger with a rotating chevron,
 * over a custom `role='listbox'` popup (`bg-ws-elevated` / `border-ws-border` / `shadow-lg`) of
 * `role='option'` buttons with arrow-key navigation — the same shape as the per-block
 * `*TriggerDropdown`s, generalized. The popup is portalled to <body> so an `overflow-hidden`
 * ancestor (e.g. a board column) can't clip it.
 */
export default function SelectMenu({
  value, options, onChange, ariaLabel, icon, disabled, className = '', fullWidth, triggerLabel,
  triggerClassName, hideChevron, header,
  fieldId, id, onKeyDown, inline = false, touch = false, align = 'start',
}: {
  value: string;
  options: SelectMenuOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  icon?: React.ReactNode;
  /**
   * The trigger's own look, replacing this menu's.
   *
   * For a trigger that is already a thing with an appearance of its own — an answer
   * reference's chip, a pill set in running prose that must keep reading as one. The menu's
   * own border and padding would make it a button sitting in a sentence.
   *
   * It REPLACES rather than appends, because the two looks have nothing in common and
   * leaving the defaults underneath means fighting them with overrides.
   */
  triggerClassName?: string;
  /** For the same case: a chevron inside prose is noise, and a chip's own label already says
   *  what it is. */
  hideChevron?: boolean;
  /**
   * Something above the options that is not one of them — see the note at the render.
   *
   * Only for a control the menu's subject needs and the list cannot express, such as renaming
   * the thing being formatted. Not a place for a label: an option that needs explaining needs
   * a better word.
   */
  header?: React.ReactNode;
  disabled?: boolean;
  /** Fill the wrapper instead of sizing to the selected label. Opt-in, because the default `w-fit`
   * is right for a board filter but wrong in a toolbar, where a long option ("Achromatopsia (no
   * colour)") grows the trigger and shunts whatever sits beside it. With this the label truncates. */
  fullWidth?: boolean;
  /** Shown on the trigger instead of the selected option's own label. For a narrow toolbar menu
   * whose options carry a parenthetical gloss: the gloss belongs in the open list, where there is
   * room, and truncating it on the trigger clips the NAME instead of the explanation. */
  triggerLabel?: string;
  /**
   * Registers the trigger as a target for `focusIssueField`, exactly as `SelectField` does.
   *
   * Carried so a native select can be replaced by this without the issues list quietly losing
   * its way to the control — "a wrong answer condition" that scrolls to nothing is worse than no
   * link at all, and nothing would have failed to say so.
   */
  fieldId?: string;
  /** Which edge of the menu lines up with the trigger's — see `openMenu`. */
  align?: 'start' | 'end';
  /** For a trigger that already has a real `<label htmlFor>` beside it. */
  id?: string;
  /**
   * Borderless and sized to its text, for a menu sitting INSIDE a sentence rather than on a form
   * row — Flow's attempt condition reads "Any attempts on this task compared how", and three
   * bordered boxes in that line turn a sentence into a form.
   *
   * Borderless, NOT quiet: the words in these menus are the only part of the sentence a teacher
   * actually sets, so they keep full contrast and weight. `SelectField` learned that the hard way
   * — it had them italic and muted, which read as grey commentary ABOUT a sentence rather than as
   * the sentence's own words. Only the chevron is muted; that is chrome, and the one part nobody
   * is choosing.
   */
  inline?: boolean;
  /**
   * A FINGER-SIZED TRIGGER, for a menu on a touch surface.
   *
   * `inline` sizes itself to its text, which is 20px tall — half the 44px this codebase treats
   * as the touch target (see `IconButton`'s `lg`, and the note on its test). That is correct in
   * a sentence on a desktop and not correct anywhere a thumb has to find it, so the height is
   * opt-in per caller rather than per variant: the same menu reads as prose on one surface and
   * as a control on another.
   *
   * Height only. The padding and borders stay the variant's, so a touch trigger still looks
   * like the thing it is rather than becoming a button.
   */
  touch?: boolean;
  /**
   * For a dropdown inside a widget with its own key handling — the media players stop
   * propagation so their transport shortcuts (space to play, arrows to scrub) do not fire while
   * the menu has focus. Attached to BOTH the trigger and the listbox, because either can hold
   * focus and the shortcut listener does not care which.
   */
  onKeyDown?: React.KeyboardEventHandler;
  className?: string;
}) {
  // The listbox needs a stable id so the trigger can point at it: a combobox without
  // `aria-controls` leaves a reader unable to follow the popup it just opened.
  const listboxId = useId();
  // The box around a header and its list. What the trigger controls is whichever element the
  // popup actually is — the box when there is a header, the list alone when there is not.
  const groupId = `${listboxId}-g`;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number; width: number }>({ top: 0, left: 0, width: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  // The outermost rendered node, which is the list itself or the box around it when there is
  // a header — it is what an outside click is measured against, so it must be whichever one
  // actually holds the menu.
  const menuRef = useRef<HTMLElement | null>(null);
  const optRefs = useRef<(HTMLButtonElement | null)[]>([]);
  /** Where the trigger was when the menu was last placed, so a scroll can be measured. */
  const anchorRef = useRef<{ top: number; left: number } | null>(null);

  function openMenu() {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    /*
     * `align='end'` pins the menu's RIGHT edge to the trigger's, instead of its left.
     *
     * The default grows rightwards from the trigger's left edge, which is right for a wide
     * trigger and wrong for a narrow one: an icon-only trigger near the right of a panel, with
     * options far wider than itself, put most of the list off the screen. Anchoring the far
     * edge is what keeps a small control's menu on screen, and it is the convention a
     * right-aligned menu follows everywhere else.
     */
    setPos(align === 'end'
      ? { top: r.bottom + 4, right: window.innerWidth - r.right, width: r.width }
      : { top: r.bottom + 4, left: r.left, width: r.width });
    anchorRef.current = { top: r.top, left: r.left };
    setOpen(true);
  }

  /*
   * AND THEN PULLED BACK ON SCREEN, once there is something to measure.
   *
   * The position above is the trigger's, which is right until the menu is wider or taller
   * than the room below and beside it. On a desktop panel that is rare; on a phone it is the
   * normal case — a chip sits wherever the sentence put it, including hard against the right
   * edge, and a landscape phone is ~370px tall, which a list of options plus a header clears
   * on its own. Both directions were off-screen with no way to scroll to them.
   *
   * Measured rather than guessed, because the menu's width is its content's and nothing knows
   * it before layout. It only ever moves a menu that does not fit, so a menu that fits is
   * positioned exactly as it was.
   */
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!open || !el) return;
    const fit = () => {
      const m = 8;
      const box = el.getBoundingClientRect();
      const dx = Math.min(0, window.innerWidth - m - box.right) || Math.max(0, m - box.left);
      // Above the trigger when there is no room below it, which is where the room is.
      const overflowsDown = box.bottom > window.innerHeight - m;
      const trigger = btnRef.current?.getBoundingClientRect();
      const flipped = overflowsDown && trigger && trigger.top - box.height - 4 >= m
        ? trigger.top - box.height - 4
        : null;
      const dy = flipped === null && overflowsDown
        ? Math.max(m - box.top, window.innerHeight - m - box.bottom)
        : 0;
      if (dx === 0 && dy === 0 && flipped === null) return;
      setPos(prev => ({
        ...prev,
        top: flipped ?? prev.top + dy,
        left: prev.left === undefined ? undefined : prev.left + dx,
        right: prev.right === undefined ? undefined : prev.right - dx,
      }));
    };
    fit();
    /*
     * AND AGAIN WHENEVER THE BOX CHANGES SIZE, because measuring once measures too early.
     *
     * A menu that grows after its first layout keeps the position computed for the smaller
     * box, and the shift it was given is the shift the SMALLER box needed. Seen on a chip's
     * popup near the right edge of the editor: it was moved 8px when it needed 16, and came
     * to rest exactly on the window's edge with the digit field hanging off it.
     *
     * No loop: this writes `left`/`top`, which cannot change the box's SIZE, and the guard
     * above returns once nothing needs moving.
     */
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
    // `open` alone: this writes `pos`, so depending on it too would re-run on its own result.
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const selectedIdx = Math.max(0, options.findIndex((o) => o.value === value));
    /*
     * THE HEADER FIRST, when there is one.
     *
     * The popup is PORTALLED to the body, so Tab from the trigger does not walk into it — it
     * walks to whatever follows the trigger in the editor. Focus has to be placed, and
     * placing it on an option left a header field reachable only by Shift+Tab, and on a chip
     * with no formats to list reachable by nothing at all: that popup's only control is the
     * field, and the keyboard never got into it.
     *
     * Only the header's own controls, never an option's button — `data-menu-header` is what
     * tells them apart. Arrow keys walk on into the list from here (`onMenuKeyDown` treats
     * "not on an option" as "before the first"), so the one gesture reaches both halves.
     */
    const raf = requestAnimationFrame(() => {
      const inHeader = menuRef.current?.querySelector<HTMLElement>(
        '[data-menu-header] input, [data-menu-header] button, [data-menu-header] select, [data-menu-header] textarea',
      );
      if (inHeader) inHeader.focus();
      else optRefs.current[selectedIdx]?.focus();
    });
    function onDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false);
    }
    /*
     * A SCROLL MOVES IT, and used to close it.
     *
     * Closing was the cheap answer to a real problem — the menu is placed from the trigger's
     * rect once, so a scroll would leave it floating beside nothing. But these menus hold
     * controls now, not just lists: the rename field lives in one, and so do the digit rows.
     * Dismissing on any scroll meant a trackpad nudge while typing threw the edit away.
     * Reported as not being able to scroll without closing the menu.
     *
     * TRANSLATED BY HOW FAR THE TRIGGER MOVED, rather than re-placed from its new rect. The
     * layout effect above clamps a menu to the viewport and flips it above its trigger when
     * there is no room below, and it runs once on purpose because it writes the position it
     * reads. Re-placing would discard that work on the first scroll and snap a flipped menu
     * back under its trigger; shifting by the delta carries it along untouched.
     *
     * ARMED A FRAME LATE, still. Opening places focus, and placing focus in a field is itself
     * something that scrolls: a phone raises its keyboard and the browser scrolls the field
     * into view. That scroll is caused by the opening and is not one the menu should react to.
     */
    function onScroll() {
      const r = btnRef.current?.getBoundingClientRect();
      const was = anchorRef.current;
      if (!r || !was) return;
      const dTop = r.top - was.top;
      const dLeft = r.left - was.left;
      if (dTop === 0 && dLeft === 0) return;
      anchorRef.current = { top: r.top, left: r.left };
      setPos(prev => ({
        ...prev,
        top: prev.top + dTop,
        left: prev.left === undefined ? undefined : prev.left + dLeft,
        // `right` is measured from the viewport's edge, so it moves against the trigger.
        right: prev.right === undefined ? undefined : prev.right - dLeft,
      }));
    }
    document.addEventListener('mousedown', onDown);
    const arm = requestAnimationFrame(() => window.addEventListener('scroll', onScroll, true));
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(arm);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function onMenuKeyDown(e: React.KeyboardEvent) {
    const refs = optRefs.current.filter((r): r is HTMLButtonElement => r !== null);
    const idx = refs.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); refs[Math.min(idx + 1, refs.length - 1)]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); refs[Math.max(idx - 1, 0)]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); refs[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); refs[refs.length - 1]?.focus(); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); btnRef.current?.focus(); }
  }

  function pick(v: string) {
    if (options.find(o => o.value === v)?.disabled) return;
    onChange(v); setOpen(false); btnRef.current?.focus();
  }

  const selected = options.find((o) => o.value === value);
  const items = (
    <>
      {options.map((o, i) => (
        <li key={o.value}>
          {/* The break before a group's first option. `aria-hidden`, because the heading is
              decoration for the eye: a reader walks options, and one that announced a heading
              between them would be reading furniture as content. */}
          {o.group && o.group !== options[i - 1]?.group && (
            <span aria-hidden='true'
              className='mt-1 block px-3 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-ws-muted first:mt-0 first:pt-0'>
              {o.group}
            </span>
          )}
          <button ref={(el) => { optRefs.current[i] = el; }} type='button' role='option' aria-selected={o.value === value} tabIndex={-1}
            aria-disabled={o.disabled || undefined}
            onClick={() => pick(o.value)}
            className={`flex w-full items-center gap-2 whitespace-nowrap px-3 py-1.5 text-left text-sm ${o.value === value ? 'font-medium' : ''} ${
              o.disabled ? 'cursor-not-allowed text-ws-muted opacity-60' : 'text-ws-text hover:bg-ws-hover'}`}>
            {/* A fixed slot, not a conditional one: with some options carrying a glyph and others
                not, labels that start at different x read as two lists stacked. */}
            {options.some(opt => opt.icon) && (
              <span aria-hidden='true' className='flex h-4 w-4 shrink-0 items-center justify-center text-ws-muted'>{o.icon}</span>
            )}
            {o.label}
          </button>
        </li>
      ))}
    </>
  );

  /*
   * A HEADER ABOVE THE LIST, OUTSIDE THE LISTBOX.
   *
   * For a menu whose first job is not choosing: an answer reference's chip renames the alias
   * from here, and a rename is a text field. A field inside `role='listbox'` is invalid — a
   * listbox holds options — and the arrow keys that walk the options would be the arrow keys
   * that move the caret. So a header gets a box of its own and the listbox stays a listbox.
   *
   * WITHOUT ONE THE DOM IS UNCHANGED, deliberately: thirteen other menus render through here
   * and none of them should gain a wrapper because one of them needed a field.
   */
  /*
   * AND A POPUP WITH NO OPTIONS AT ALL, which is the header on its own.
   *
   * A reference to a plain number has no second way to be read, so it offers no formats — and
   * it still has a name to edit and an address to show, which is the whole reason the popup
   * exists on every chip and not only on the ones with something to choose. An empty listbox
   * would be a box of nothing under the field, and a trigger announcing a listbox that holds
   * no options would be announcing something untrue, so neither is rendered.
   */
  const hasOptions = options.length > 0;
  /*
   * ABOVE EVERY SURFACE IT CAN BE OPENED FROM, which is what `z-50` was not.
   *
   * This menu portals to the body, so its stacking is decided against the whole page rather
   * than against the thing it belongs to. A phone opens a block's editor as a full-screen
   * `z-[100]` sheet, so a menu inside that editor rendered BEHIND it: present in the DOM,
   * correctly positioned, measurable, and underneath an opaque surface. Tapping a chip on a
   * phone looked like nothing happening at all.
   *
   * 140 clears the tallest thing in the app — the sensor sheet at `z-[130]` — and the rule it
   * follows is `flow-picker-menu`'s, which already picked `z-[120]` to clear the `z-[110]`
   * dialog it opens from. `select-menu.test.tsx` holds the ceiling to this.
   *
   * `max-w` so a long line cannot make the box wider than the screen in the first place — the
   * clamp above can move a menu back on screen but cannot shrink one wider than it.
   */
  const panel = `fixed ${POPUP_LEVEL} max-w-[calc(100vw-16px)] rounded-lg border border-ws-border bg-ws-elevated shadow-lg`;
  const list = 'max-h-60 overflow-y-auto py-1';
  /*
    * AT LEAST AS WIDE AS ITS TRIGGER — unless the caller brought its own trigger.
    *
    * The floor exists so a select's menu is never narrower than the control it drops from.
    * A caller's own trigger is not a control of this menu's making and can be any width: a
    * recall chip showing its full address is most of a line, and the menu inherited that as
    * a MINIMUM, so a long chip produced a menu far wider than its own contents and pushed
    * the digit field off the side. Reported as the menu being too wide with the input off
    * screen.
    *
    * `triggerClassName` is the signal, because it is already what "I bring my own trigger"
    * means here — the same prop that replaces this menu's trigger look entirely.
    */
  const place = {
    top: pos.top, left: pos.left, right: pos.right,
    minWidth: triggerClassName ? undefined : pos.width,
  };
  const keys = (e: React.KeyboardEvent) => { onKeyDown?.(e); onMenuKeyDown(e); };

  const menu = open && createPortal(
    header
      ? (
        /* `group`, because it is one: a field and the list it belongs to, announced as a
           unit rather than as two things that happen to be adjacent. */
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- not a control: it catches Escape and the click BUBBLING from the real field and options inside it, the same arrangement `variables-panel` settled on
        <div ref={el => { menuRef.current = el; }} id={groupId} style={place} className={panel}
          role='group' aria-label={ariaLabel}
          onKeyDown={keys} onClick={(e) => e.stopPropagation()}>
          <div data-menu-header className={hasOptions ? 'border-b border-ws-border p-1.5' : 'p-1.5'}>
            {header}
          </div>
          {hasOptions
            && <ul id={listboxId} role='listbox' aria-label={ariaLabel} className={list}>{items}</ul>}
        </div>
      )
      : (
        <ul ref={el => { menuRef.current = el; }} id={listboxId} role='listbox' aria-label={ariaLabel}
          onKeyDown={keys} onClick={(e) => e.stopPropagation()}
          style={place} className={`${panel} ${list}`}>
          {items}
        </ul>
      ),
    document.body,
  );

  return (
    <div className={`inline-block ${className}`}>
      {/*
        `role='combobox'`, because that is what a collapsed listbox selector is.
        Without it the trigger announced as a plain button and a reader was never told WHICH
        option was chosen — the native `<select>` this replaces said "Playback speed, combo box,
        1x". The visible label inside the trigger is the value; `aria-label` is the name.
      */}
      <button ref={btnRef} type='button' role={hasOptions ? 'combobox' : undefined} disabled={disabled}
        aria-haspopup={hasOptions ? 'listbox' : 'dialog'} aria-expanded={open}
        aria-controls={header ? groupId : listboxId} aria-label={ariaLabel}
        id={id} data-field-kind='select' data-field-id={fieldId} onKeyDown={onKeyDown}
        onClick={() => (open ? setOpen(false) : openMenu())}
        /* `max-w-full min-w-0` so the label's own `truncate` can actually engage. `w-fit` sizes the
           trigger to its content with nothing to stop it, so a long selection — a reaction or an
           equation picked as a feedback trigger — grew the button straight past the panel edge and
           off screen. Capping it at the parent lets the span below shrink and ellipsise instead.
           Fixed here rather than at one call site: any menu can be handed a long label. */
        className={triggerClassName ?? `inline-flex ${fullWidth ? 'w-full' : 'w-fit'} min-w-0 max-w-full items-center gap-1 rounded font-medium text-ws-text focus:outline-none disabled:opacity-50 ${touch ? 'min-h-11' : ''} ${
          inline
            ? 'bg-transparent px-1 py-0.5 text-xs hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
            : 'gap-1.5 border border-ws-border bg-ws-btn px-2.5 py-1.5 text-sm hover:bg-ws-hover focus:border-ws-primary'
        }`}>
        {icon}
        {/* A caller's own trigger brings its own layout, so the flex filler goes with this
            menu's own look rather than squeezing a chip's label into a column. */}
        {triggerClassName
          ? (triggerLabel ?? selected?.label ?? '')
          : <span className='flex-1 truncate text-left'>{triggerLabel ?? selected?.label ?? ''}</span>}
        {!hideChevron && (
          <ChevronDown aria-hidden='true'
            className={`h-3.5 w-3.5 shrink-0 transition-transform ${inline ? 'text-ws-muted' : 'opacity-50'} ${open ? 'rotate-180' : ''}`} />
        )}
      </button>
      {menu}
    </div>
  );
}
